import React, { useEffect, useState } from 'react';
import { Form, Image } from 'react-bootstrap';
import CommonModal from '../modalLayout/CommonModal';
import Button from '../buttons/button';
import InputWithLabel from '../fields/InputWithLabel';
import {
  useAddProductMutation,
  useUpdateProductMutation,
  useGetProductByIdQuery
} from '../../../app/offerApi/OfferApi';
import { showToast } from '../../../helpers/Utils';

const initialFormState = {
  product_name: '',
  brand: '',
  product_price: '',
  discount: false,
  discount_percentage: '',
  product_description: '',
  product_image: null,
};

const AddOfferModal = ({ show, setShow, prodcutId }) => {
  const [formState, setFormState] = useState(initialFormState);
  const [imagePreview, setImagePreview] = useState(null); // New state for image preview

  const [createProduct, { isLoading: creating }] = useAddProductMutation();
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
  const { data: productData, isLoading: loadingProduct, isFetching } = useGetProductByIdQuery(prodcutId, {
    skip: !prodcutId,
  });

  useEffect(() => {
    if (prodcutId && productData?.data) {
      setFormState({ ...initialFormState, ...productData.data });
      // Set image preview from existing product image URL
      if (productData.data.product_image) {
        setImagePreview(productData.data.product_image);
      }
    } else if (!prodcutId && show) {
      setFormState(initialFormState);
      setImagePreview(null); // Reset preview when adding new product
    }
  }, [prodcutId, productData, show]);

  const handleChange = ({ target: { name, value, type, checked, files } }) => {
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormState(prev => ({ ...prev, [name]: file }));
        // Generate preview URL
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
      }
    } else {
      setFormState(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();

    Object.entries(formState).forEach(([key, val]) => {
      if (key === 'product_image' && val instanceof File) {
        fd.append(key, val);
      } else if (key !== 'product_image') {
        fd.append(key, val?.toString() || '');
      }
    });

    const requiredFields = ['product_name', 'brand', 'product_price', 'product_description'];
    if (requiredFields.some(field => !formState[field])) {
      return showToast('Please fill all required fields.', 'error');
    }

    try {
      if (prodcutId) {
        await updateProduct({ productId: prodcutId, formData: fd }).unwrap();
      } else {
        await createProduct(fd).unwrap();
        setFormState(initialFormState);
        setImagePreview(null); // Reset preview after creation
      }
      setShow(false);
    } catch (error) {
      console.error(error);
      showToast(error?.data?.message || 'Something went wrong', 'error');
    }
  };

  return (
    <CommonModal show={show} setShow={setShow} title={prodcutId ? 'Edit Product' : 'Add Product'}>
      {((loadingProduct && prodcutId) || isFetching) ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <Form onSubmit={handleSubmit} encType="multipart/form-data">
          <InputWithLabel label="Product Name" name="product_name" value={formState.product_name} onChange={handleChange} required />
          <InputWithLabel label="Brand" name="brand" value={formState.brand} onChange={handleChange} required />
          <InputWithLabel label="Price" name="product_price" type="number" value={formState.product_price} onChange={handleChange} required />

          <Form.Group className="mb-3">
            <Form.Check type="checkbox" name="discount" label="Apply Discount" checked={formState.discount} onChange={handleChange} />
          </Form.Group>

          {formState.discount && (
            <InputWithLabel label="Discount %" name="discount_percentage" type="number" value={formState.discount_percentage} onChange={handleChange} />
          )}

          <Form.Group className="mb-3">
            <Form.Label>Image</Form.Label>
            <Form.Control type="file" name="product_image" accept="image/*" onChange={handleChange} />
          </Form.Group>

          {/* Show image preview if available */}
          {imagePreview && (
            <div className="mb-3">
              <Image src={imagePreview} alt="Image Preview" fluid width={100} height={100} />
            </div>
          )}

          {/* Show existing image if editing and no new image selected */}
          {formState.product_image && typeof formState.product_image === 'string' && !imagePreview && (
            <Image src={formState.product_image} alt="Product" fluid width={100} height={100} />
          )}

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" name="product_description" value={formState.product_description} onChange={handleChange} required />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button
              label={
                prodcutId
                  ? updating ? 'Updating...' : 'Update Product'
                  : creating ? 'Creating...' : 'Create Product'
              }
              type="submit"
              disabled={creating || updating}
              variant="primary"
            />
          </div>
        </Form>
      )}
    </CommonModal>
  );
};

export default AddOfferModal;